import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { BackupStatus } from "../api/types";
import { SpecimenCatalogSettings } from "../components/SpecimenCatalogSettings";
import { formatBackupTimestamp } from "../lib/dates";

function bytesLabel(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function SettingsPage() {
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await api<BackupStatus>("/api/portal/backup");
    setStatus(data);
    setError("");
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [load]);

  async function runBackup() {
    setBusy(true);
    setError("");
    try {
      await api("/api/portal/backup", { method: "POST" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <div>
          <strong>Settings</strong>
        </div>
        <div className="toolbar-actions">
          <button type="button" className="tool-btn" onClick={() => load().catch((e) => setError(e.message))}>
            Refresh
          </button>
        </div>
      </div>
      {error && <div className="banner-error">{error}</div>}

      <SpecimenCatalogSettings />

      <section className="settings-card">
        <h3>Backup</h3>
        {status?.schedule ? <p className="muted">{status.schedule}</p> : null}
        <div className="record-grid">
          <div className="field-block">
            <div className="field-label">Wasabi</div>
            <div className="field-value">
              {status == null ? "—" : status.configured ? status.bucket || "Wasabi" : "Wasabi credentials not configured"}
            </div>
          </div>
        </div>
        <button type="button" className="primary" disabled={busy || !status?.configured} onClick={() => void runBackup()}>
          {busy ? "Working…" : "Run backup"}
        </button>
        <ul className="backup-list">
          {(status?.backups || []).map((b) => (
            <li key={b.timestamp}>
              <span>{formatBackupTimestamp(b.timestamp)}</span>
              <span className="muted">{bytesLabel(b.total_bytes)}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
